import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, Gift, Check, Trophy } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { deschideFisier } from '../../lib/downloadFile'
import { Card } from '../../components/ui/Card'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { SemnaturaPad } from '../../components/SemnaturaPad'

/**
 * Vederea de stand: număr, nume, telefon. Atât.
 *
 * Cerută de Victor: oamenii care stau la stand au nevoie să vadă baza, dar nu toată. Restricția
 * nu e doar de ecran — serverul are un endpoint separat, care întoarce exact cele trei câmpuri.
 * Ce nu pleacă de pe server nu se poate scurge dintr-un ecran.
 *
 * Gândită pentru telefon, ținut în mână, într-un zgomot de târg: câmpuri mari, un singur lucru
 * pe ecran, iar căutarea acceptă și numărul — „cine e 155?" e întrebarea care se pune de fapt,
 * după fiecare extragere.
 */

interface Inscriere {
  nrOrdine: number
  predareId: number
  /** Când s-a înscris. */
  inscrisLa: string
  nume: string
  telefon: string | null
  /** Ce a scris omul în formular, la „Observații (opțional)”. */
  observatii: string | null
  /** Marfa scrisă pe înscriere înseamnă marfă dată. Fără câmp în plus de ținut minte. */
  cadouPredat: boolean
  /** A primit chiar la semnătură, din formular. Nu e de căutat aici. */
  predatLaSemnatura: boolean
  premiuPredat: boolean
  /** A bifat în formular că vrea cadoul, știind condiția. */
  cadouCerut: boolean
  /** A fost extras la tombolă. Doar lui i se cuvine premiul. */
  castigator: boolean
  /** Extragerea care l-a scos, ca să se poată deschide procesul-verbal de extragere. */
  extragereId: number | null
  /** Ora extragerii: de la ea curg cele două ore de ridicare (art. 6.2). */
  extragereLa: string | null
  /** Când s-a constatat că premiul n-a fost ridicat. */
  neridicatLa: string | null
  anulata: boolean
  nrPv: string | null
  /** Bonul de consum emis în ExpertAccounts. `null` = n-a ieșit nimic din gestiune. */
  bonEaDocNo: string | null
  /** S-a declarat că bonul a fost șters sau stornat în Expert. Până atunci ștergerea e oprită. */
  bonStornat: boolean
}

interface ProdusCadou {
  productId: number
  denumire: string
  /**
   * Cat s-a ALOCAT pe toata actiunea, nu cat primeste fiecare om (Alexandru, 24.09.2026).
   *
   * La premiu inseamna altceva: compozitia kitului, „1 shaker + 1 creatina", care se da intreaga.
   */
  cantitate: number
  /** Cat a plecat pana acum din el, pe toata actiunea. */
  predat: number
  rol: 'cadou' | 'premiu'
}

interface ActiuneScurta {
  id: number
  denumire: string
  tip: string
}

/**
 * Ecran tactil? De asta depinde daca numarul de telefon e link sau text.
 *
 * Pe desktop un `tel:` deschide fereastra „Open Pick an app?" a browserului, care nu duce nicaieri
 * si trebuie inchisa de fiecare data. La stand se lucreaza de pe telefon, unde linkul chiar suna.
 *
 * Se citeste o data, la incarcare: nu se schimba tipul de ecran in timpul unui eveniment.
 */
const eTactil = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches

function zi(d: Date): string {
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: 'Europe/Bucharest',
    day: '2-digit',
    month: '2-digit',
  }).format(d)
}

function oraS(d: Date): string {
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: 'Europe/Bucharest',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(d)
}

/**
 * Ora înscrierii. Ziua se scrie doar când nu e azi.
 *
 * Alexandru, dupa ce a vazut ecranul: „ziua apare doar când nu e azi — e ok asta, nu văzusem".
 * Pe coloana asta se citesc trei sute de rânduri deodată, iar „29.08" repetat pe fiecare ar fi
 * doar zgomot. Când rândul e de ieri, ziua apare — și atunci chiar spune ceva.
 */
function candS(iso: string): string {
  const d = new Date(iso)
  return zi(d) === zi(new Date()) ? oraS(d) : `${zi(d)} ${oraS(d)}`
}

/**
 * Data ȘI ora, întotdeauna. Pentru cronologia premiului neridicat.
 *
 * Acolo se compară două momente și se numără orele dintre ele — Alexandru: „să se vadă în plus și
 * data și ora la care a fost extragerea, data la care a fost marcat ca premiu neridicat". O oră
 * fără zi lasă întrebarea deschisă exact când se pune.
 */
function ziSiOraS(iso: string): string {
  const d = new Date(iso)
  return `${zi(d)} ${oraS(d)}`
}

/**
 * Cât mai are câștigătorul până expiră cele două ore.
 *
 * Victor: „putem pune și un cronometru? Să ne dăm seama de cele 2 ore". Art. 6.2 — termenul curge
 * de la ora exactă a extragerii, consemnată în CRM și pe procesul-verbal, și nu se suspendă.
 *
 * Se calculează din ora extragerii, nu dintr-un ceas pornit la deschiderea paginii: cine deschide
 * ecranul la 14:00 trebuie să vadă același timp rămas ca cel care l-a deschis la 13:00.
 */
function dupaCatTimp(extrasLa: string, marcatLa: string): { text: string; peste: boolean } {
  const min = Math.round((new Date(marcatLa).getTime() - new Date(extrasLa).getTime()) / 60_000)
  const h = Math.floor(min / 60)
  return { text: h > 0 ? `${h}h ${min % 60}m` : `${min} min`, peste: min >= 120 }
}

/** A primit deja, pe rolul deschis in tab? Se citeste din marfa scrisa pe inscriere. */
function predatPe(i: Inscriere, rol: 'cadou' | 'premiu'): boolean {
  return rol === 'premiu' ? i.premiuPredat : i.cadouPredat
}

function ceasDouaOre(extragereLa: string): { text: string; expirat: boolean } {
  const limita = new Date(extragereLa).getTime() + 2 * 60 * 60 * 1000
  const min = Math.round((limita - Date.now()) / 60_000)
  if (min <= 0) return { text: `expirat de ${-min} min`, expirat: true }
  const h = Math.floor(min / 60)
  return { text: h > 0 ? `${h}h ${min % 60}m rămase` : `${min} min rămase`, expirat: false }
}

export function SamplingStandPage() {
  /*
    `?actiune=<id>` deschide ecranul direct pe acțiunea cerută.

    Ca să ajungă cineva aici din evenimentul lui: din fila Teren, fiecare acțiune are „Vedere de
    stand". Fără parametru, ecranul cădea pe prima acțiune din listă, iar la stand — cu telefonul
    în mână și un om în față — asta înseamnă să cauți acțiunea potrivită într-un selector
    (Alexandru, 22.09.2026).
  */
  /*
    Stergerea unei inscrieri, doar pentru admini.

    Ecranul de stand e locul unde se vad inscrierile pe masura ce intra, deci si locul unde se
    observa ca una e de proba sau gresita. Alexandru, 22.09.2026: „as vrea ca adminii sa poata
    sterge aici". Serverul are deja plasa: o predare cu bon emis si nestornat nu se sterge.
  */
  const eAdmin = useAuthStore((s) => s.user?.role) === 'admin'
  const [params] = useSearchParams()
  const dinUrl = Number(params.get('actiune'))
  const [actiuneId, setActiuneId] = useState<number | null>(
    Number.isFinite(dinUrl) && dinUrl > 0 ? dinUrl : null,
  )
  const [cauta, setCauta] = useState('')
  /*
    „Doar castigatorii". Alexandru: „pune aici un filtru sa pot sa vad doar castigatorii".

    La trei sute de inscrieri, cei cinci care au castigat sunt imprastiati printre ele, iar pe
    tab-ul de premii restul randurilor n-au nici macar buton — sunt acolo doar ca sa se poata cauta
    „cine e 155?". Filtrul le da la o parte.

    Se aprinde singur cand treci pe Premii tombola si se stinge la loc pe Recompensa story: pe
    primul tab e intrebarea implicita, pe al doilea ar ascunde tocmai oamenii care vin la stand.
  */
  const [doarCastigatori, setDoarCastigatori] = useState(false)
  /*
    Un tic din minut in minut, doar ca sa se redeseneze ceasul celor doua ore. Nu cere nimic de la
    server: timpul ramas se calculeaza din ora extragerii, care nu se schimba.
  */
  const [, setTic] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTic((x) => x + 1), 30_000)
    return () => clearInterval(t)
  }, [])

  const actiuni = useQuery({
    queryKey: ['stand', 'actiuni'],
    queryFn: async () => (await api.get<ActiuneScurta[]>('/marketing/sampling/stand/actiuni')).data,
  })

  const alesa = actiuneId ?? actiuni.data?.[0]?.id ?? null

  const date = useQuery({
    queryKey: ['stand', 'inscrieri', alesa],
    queryFn: async () =>
      (
        await api.get<{
          denumire: string
          /** Tipul actiunii: ecranul trebuie sa stie daca e tombola, fara sa caute in alta lista. */
          tip: string
          areCadou: boolean
          arePremiu: boolean
          conditieCadou: string | null
          produse: ProdusCadou[]
          inscrieri: Inscriere[]
        }>(`/marketing/sampling/stand/actiuni/${alesa}/inscrieri`)
      ).data,
    enabled: alesa !== null,
    // La stand se înscrie lume tot timpul; lista trebuie să fie a momentului, nu a minutului
    // în care s-a deschis pagina.
    refetchInterval: 30_000,
  })

  /*
    Predarea cadoului. Se rupe in doua momente pentru ca asa se intampla la stand: omul se
    inscrie cand scaneaza, si se intoarce mai tarziu cu Story-ul postat. Verificarea o face
    echipa, cu ochii; aplicatia se ocupa de ce urmeaza dupa — semnatura, PV, bon.
  */
  /*
    Cele doua fluxuri de acordare, in tab-uri. Alexandru: „as grupa aceste 2 fluxuri in meniul
    Sampling — stand (...) deci aici as vedea 2 tab-uri".

    Amandoua se termina la fel — verifici, alegi, semneaza, PV, bon de consum — dar dau marfa
    diferita si se cuvin unor oameni diferiti: unul cui a postat un Story, celalalt cui a fost
    extras. Regulamentul le tine separate de la preambul; ecranul face la fel.

    Extragerea NU se muta aici: e un act administrativ, cu comisie (art. 6.2), care produce un
    proces-verbal de extragere. Vederea de stand intoarce trei campuri si atat, tocmai ca cine sta
    la stand sa nu vada restul. Standul preda, administrarea extrage.
  */
  const [tab, setTab] = useState<'cadou' | 'premiu'>('cadou')
  const [deSemnat, setDeSemnat] = useState<Inscriere | null>(null)
  const [semnatura, setSemnatura] = useState<string | null>(null)
  /*
    Conditia verificata cu ochii, si produsul ales. Amandoua se reseteaza la fiecare deschidere:
    o bifa ramasa de la omul dinainte ar consemna o verificare care nu s-a facut.
  */
  const [storyOk, setStoryOk] = useState(false)
  const [produsAles, setProdusAles] = useState<number | ''>('')
  /*
    Ce a bifat agentul si cat da din fiecare: `productId -> cantitate`.

    Inlocuieste lista derulanta cu un singur produs. La farmacie omul pleaca de multe ori cu
    doua-trei mostre, iar pe ecranul vechi nu incapeau pe acelasi proces-verbal — trebuia ales
    unul. Alexandru: „ma gandeam ca asta se face prin bifare".

    Cantitatea porneste de la 1, nu de la cea configurata pe actiune: cifra de acolo inseamna cat
    s-a alocat pe TOATA actiunea, iar copiata aici primul venit ar fi plecat cu toate.
  */
  const [bifate, setBifate] = useState<Map<number, number>>(new Map())
  const queryClient = useQueryClient()

  /*
    Declararea stornarii, ca sa se poata sterge o inscriere cu bon emis.

    Refuzul stergerii spune „apasa «Bon stornat»". Butonul exista, dar pe alte doua ecrane — in
    modulul Actiuni, fila Predari, si in fila Predari a campaniei. Alexandru a intrebat „unde apas
    Bon stornat?" pe 22.09.2026 din primul loc, si din nou pe 24.09.2026 de aici. Un mesaj care
    arata spre un buton de pe alta pagina nu e un drum; a treia oara nu mai are rost sa se repete.
  */
  const bonStornat = useMutation({
    mutationFn: async (predareId: number) =>
      api.post(`/marketing/sampling/predari/${predareId}/bon-stornat`, {}),
    onSuccess: () => {
      toast.success('Notat. Acum înscrierea se poate șterge.')
      void queryClient.invalidateQueries({ queryKey: ['stand', 'inscrieri'] })
    },
    onError: (e) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err.response?.data?.error?.message ?? 'Nu am putut nota stornarea')
    },
  })

  /* Vezi `eAdmin`: ștergerea unei înscrieri de probă, direct de pe rândul ei. */
  const stergeInscrierea = useMutation({
    mutationFn: async (predareId: number) =>
      (await api.delete<{ nrPv: string | null }>(`/marketing/sampling/predari/${predareId}`)).data,
    onSuccess: (r) => {
      toast.success(`Înscrierea ${r.nrPv ?? ''} a fost ștearsă`.trim())
      void queryClient.invalidateQueries({ queryKey: ['stand', 'inscrieri'] })
    },
    onError: (e) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err.response?.data?.error?.message ?? 'Înscrierea nu s-a putut șterge')
    },
  })

  const predaCadou = useMutation({
    mutationFn: async (v: {
      predareId: number
      semnatura: string
      /** Lipsește când acțiunea n-are nicio condiție scrisă: nu e nimic de declarat. */
      storyOk?: boolean
      /** Bifate, cu cantitatea fiecăruia. Calea obișnuită la mostre. */
      produse?: { productId: number; cantitate: number }[]
      /** Un singur produs. Rămâne pentru cadoul de tombolă, neatins. */
      productId?: number
      rol: 'cadou' | 'premiu'
    }) =>
      api.post(`/marketing/sampling/stand/predari/${v.predareId}/cadou`, {
        semnatura: v.semnatura,
        ...(v.storyOk === undefined ? {} : { storyOk: v.storyOk }),
        rol: v.rol,
        ...(v.produse?.length ? { produse: v.produse } : {}),
        ...(v.productId ? { productId: v.productId } : {}),
      }),
    onSuccess: () => {
      toast.success(
        tab === 'premiu'
          ? 'Premiu predat. Procesul-verbal pleacă pe email.'
          : 'Predat. Procesul-verbal pleacă pe email.',
      )
      setDeSemnat(null)
      setSemnatura(null)
      setStoryOk(false)
      setProdusAles('')
      setBifate(new Map())
      void queryClient.invalidateQueries({ queryKey: ['stand', 'inscrieri'] })
    },
    onError: (e) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err.response?.data?.error?.message ?? 'Nu s-a putut preda cadoul')
    },
  })

  /*
    Ce fluxuri are actiunea. La un sampling simplu — mostre in farmacie — n-are niciunul: nu se
    recompenseaza un Story si nu se extrage nimeni.
  */
  /** Tipul actiunii deschise. Decide cuvintele de pe ecranul de predare. */
  const tipActiune = actiuni.data?.find((a) => a.id === alesa)?.tip ?? null
  /*
    Tombola isi pastreaza ecranul vechi de predare: lista derulanta, un singur produs, cantitatea
    din configurare. Bifarea cu cantitati e pentru mostre (Alexandru, 24.09.2026: „le las exact
    cum sunt").

    Tipul vine si din vederea de stand, nu doar din lista de actiuni: lista se poate filtra, iar
    un ecran deschis direct pe `?actiune=31` n-ar fi gasit actiunea in ea.
  */
  const eTombola = (date.data?.tip ?? tipActiune) === 'tombola'
  /*
    Conditia de acordare, scrisa pe actiune (`textCadou`).

    Se arata DOAR daca exista. Pana pe 22.09.2026 ecranul cerea „Condiția e îndeplinită — Story cu
    @boost4life.ro" pe orice actiune, fiindca textul acela era scris in cod ca varianta implicita.
    La o vizita in farmacie nu exista nicio conditie de Story, iar bifa bloca predarea pana cand
    consultantul declara indeplinit ceva ce nimeni nu ceruse. Alexandru: „conditia cu story nu are
    ce cauta aici".
  */
  const conditieAcordare = date.data?.conditieCadou?.trim() || null
  const areCadou = date.data?.areCadou ?? false
  const arePremiu = date.data?.arePremiu ?? false
  /* Daca tabul deschis nu se aplica, trecem pe cel care exista. */
  useEffect(() => {
    if (tab === 'cadou' && !areCadou && arePremiu) setTab('premiu')
    if (tab === 'premiu' && !arePremiu && areCadou) setTab('cadou')
  }, [tab, areCadou, arePremiu])

  /** Produsele rolului deschis in tab. La premiu sunt componentele kitului, nu cadoul. */
  const produseRol = (date.data?.produse ?? []).filter((x) => x.rol === tab)

  const termen = cauta.trim().toLowerCase()
  /*
    CEI MAI NOI PRIMII. Alexandru: „poate aici ar fi mai bine ca ultimii inscrisi sa fie afisati
    primii?".

    Are dreptate, si nu doar ca sa se scuteasca derularea: omul din fata operatorului tocmai s-a
    inscris. Cu ordinea veche, la sapte sute de randuri, ca sa ajungi la cel de acum trebuia sa
    derulezi tot evenimentul — sau sa-i ceri numarul, pe care jumatate nu si-l amintesc.

    Numarul de ordine NU se schimba: el ramane cel al inscrierii, fiindca pe el se face extragerea
    si el e scris pe procesul-verbal. Se schimba doar ordinea in care sunt asezate randurile.
  */
  const lista = [...(date.data?.inscrieri ?? [])].reverse().filter((i) => {
    if (doarCastigatori && !i.castigator) return false
    if (!termen) return true
    return (
      String(i.nrOrdine) === termen ||
      i.nume.toLowerCase().includes(termen) ||
      (i.telefon ?? '').includes(termen)
    )
  })

  return (
    /*
      Latime limitata. Ecranul e gandit pentru telefon, dar se deschide si pe desktop, iar acolo
      randul se intindea pe toata latimea: numele la stanga, butonul de premiu tocmai la marginea
      dreapta, cu un metru de gol intre ele. Pe un monitor lat nici nu se mai vedea ca sunt pe
      acelasi rand.
    */
    <div className="mx-auto max-w-3xl space-y-3">
      {/*
        Antetul ramane pe ecran: titlu, tab-uri, selectorul de actiune si caseta de cautare.
        Alexandru: „as vrea sa se faca freeze pana la caseta de cautare inclusiv".

        Lista de inscrisi creste cu fiecare om care scaneaza; la cateva sute de randuri, ca sa
        cauti pe altcineva trebuia derulat inapoi pana sus. La stand, cu telefonul in mana si
        omul in fata ta, drumul ala e chiar timpul de asteptare al cozii.

        Zona de derulare e `<main>` din AppShell, care are p-4/md:p-6. De aici marginile negative
        (`-mx-4 -mt-4`) plus padding-ul pus la loc inauntru: la pozitia de repaus arata exact ca
        inainte, iar cand se lipeste sus acopera toata latimea coloanei, ca randurile sa nu se
        vada trecand pe sub el. Fundalul e cel al lui `<main>`, nu transparent — altfel textul
        listei s-ar citi prin antet.
      */}
      <div className="sticky top-0 z-20 -mx-4 -mt-4 space-y-3 border-b border-b4l-border bg-b4l-app px-4 pb-3 pt-4 md:-mx-6 md:-mt-6 md:px-6 md:pt-6">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Predare la stand</h1>
          {/*
            Pe CARE actiune esti. Ecranul deschis din meniu cade pe prima din lista, iar
            Alexandru s-a uitat la 1.046 de inscrieri de la Sense4FIT crezand ca sunt ale
            farmaciei (22.09.2026). Numele actiunii, scris mare, raspunde inainte de intrebare.
          */}
          {date.data?.denumire && (
            <p className="mt-0.5 text-sm font-medium text-b4l-text">{date.data.denumire}</p>
          )}
          <p className="mt-0.5 text-sm text-b4l-text-muted">
            Numărul, numele și telefonul celor înscriși.
            {arePremiu ? ' Caută după număr ca să afli cine a câștigat.' : ''} Predarea se semnează
            aici și produce proces-verbal și bon de consum.
          </p>
        </div>

        {/*
          Cele doua fluxuri de acordare, ca tab-uri. Se schimba CE se preda si CUI: „Recompensa
          story" merge la cine a postat, „Premii tombola" la cine a fost extras.

          Tab-ul de premii apare doar cand actiunea chiar are premii configurate — altfel ar
          promite un buton care n-ar avea ce da.
        */}
        {/*
          Culori diferite pe cele doua tab-uri. Alexandru: „coloreaza diferit tab-urile Recompensa
          story si Premii tombola, pentru diferentiere".

          Nu e infrumusetare: la stand se lucreaza in graba, iar cele doua tab-uri arata IDENTIC si
          duc la marfa diferita. Cine preda un kit crezand ca da o recompensa scade din gestiune
          altceva decat crede, iar procesul-verbal pleaca pe numele altui document. Verde pentru
          recompensa, chihlimbar pentru premii — plus pictograma, ca sa se recunoasca si dintr-o
          privire piezisa.
        */}
        {/*
          Se arata DOAR fluxurile pe care actiunea le are.

          Banda apare doar cand chiar ai intre ce alege. La un sampling in farmacie nu exista nici
          recompensa de story, nici tombola: se preda mostra si atat. Cele doua tab-uri stateau
          acolo oricum, iar „Premii tombola" ducea la o lista goala si la un avertisment despre
          premii neconfigurate — pe un ecran folosit in graba, in fata omului (Alexandru,
          22.09.2026).
        */}
        {areCadou && arePremiu && (
          <div className="flex gap-1 rounded-lg bg-b4l-bg p-1">
            {(
              [
                [
                  'cadou',
                  'Recompensă story',
                  'bg-b4l-green-light text-b4l-green-dark',
                  'text-b4l-green-dark',
                ],
                [
                  'premiu',
                  'Premii tombolă',
                  'bg-b4l-amber-light text-b4l-amber-dark',
                  'text-b4l-amber-dark',
                ],
              ] as const
            )
              .filter(([cheie]) => (cheie === 'cadou' ? areCadou : arePremiu))
              .map(([cheie, eticheta, activ, inactiv]) => (
                <button
                  key={cheie}
                  onClick={() => {
                    setTab(cheie)
                    setDoarCastigatori(cheie === 'premiu')
                  }}
                  /*
                Culoarea se vede si cand tab-ul NU e ales. Prima variantă o punea doar pe cel
                activ, deci Alexandru vedea „Recompensă story" tot crem: „nu e verde, arata asa".
                Or tocmai tab-ul pe care NU esti trebuie sa se recunoasca — pe el il cauti cand
                iti dai seama ca esti in locul gresit.
              */
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm ${
                    tab === cheie ? `${activ} font-medium shadow-sm` : `${inactiv} opacity-60`
                  }`}
                >
                  {cheie === 'cadou' ? <Gift size={15} /> : <Trophy size={15} />}
                  {eticheta}
                </button>
              ))}
          </div>
        )}

        {tab === 'premiu' && !arePremiu && (
          <p className="rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
            Acțiunea nu are premii configurate. Se aleg în panoul acțiunii, la „Premiul tombolei”.
          </p>
        )}

        {/*
          Selectorul apare de la PRIMA actiune, nu de la a doua: cand exista una singura, el e
          singurul lucru care spune pe ce lucrezi.
        */}
        {(actiuni.data?.length ?? 0) > 0 && (
          <select
            value={alesa ?? ''}
            onChange={(e) => setActiuneId(Number(e.target.value))}
            className="h-12 w-full rounded-lg border border-b4l-border bg-white px-3 text-base"
          >
            {actiuni.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.denumire}
              </option>
            ))}
          </select>
        )}

        <div className="relative">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-b4l-text-dim"
          />
          <input
            value={cauta}
            onChange={(e) => setCauta(e.target.value)}
            inputMode="search"
            placeholder="Număr, nume sau telefon"
            className="h-12 w-full rounded-lg border border-b4l-border bg-white pl-9 pr-3 text-base"
          />
        </div>

        {/*
          Filtrul are inteles doar pe tabul de premii. Alexandru: „daca bifez Doar castigatorii, se
          vad cei care au castigat la tombola" — pe Recompensa story, unde vine cine a postat un
          Story, cei sapte castigatori ai tombolei n-au nicio legatura cu ce se preda acolo.
        */}
        <label
          className={`flex items-center gap-2 text-sm text-b4l-text ${
            tab === 'premiu' ? '' : 'hidden'
          }`}
        >
          <input
            type="checkbox"
            checked={doarCastigatori}
            onChange={(e) => setDoarCastigatori(e.target.checked)}
            className="h-5 w-5"
          />
          Doar câștigătorii
          {doarCastigatori && (
            <span className="text-xs text-b4l-text-muted">
              ({lista.length} din {date.data?.inscrieri.length ?? 0})
            </span>
          )}
        </label>
      </div>

      <Card>
        {actiuni.isLoading || date.isLoading ? (
          <p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : !actiuni.data?.length ? (
          <p className="p-4 text-sm text-b4l-text-muted">Nicio acțiune deschisă.</p>
        ) : lista.length === 0 ? (
          <p className="p-4 text-sm text-b4l-text-muted">
            {termen ? `Nimic pentru „${termen}”.` : 'Nicio înscriere încă.'}
          </p>
        ) : (
          <div className="divide-y divide-b4l-border">
            {lista.map((i) => (
              /*
                Randul cu premiul neridicat se vede din derulare. Alexandru: „cred ca ar fi bine sa
                evidentiezi vizual pe cei cu premii neridicate".

                Are rost mai ales pe lista filtrata la castigatori, unde toate randurile arata la
                fel: sapte nume, sapte cupe, iar singura diferenta — cine mai are premiu de ridicat
                si cine nu — statea scrisa marunt, pe al treilea rand.
              */
              <div
                key={i.nrOrdine}
                className={`flex items-center gap-3 px-3 py-3 ${
                  tab === 'premiu' && i.neridicatLa
                    ? 'border-l-4 border-l-b4l-amber bg-b4l-amber-light/40'
                    : ''
                }`}
              >
                <span className="w-12 shrink-0 text-right font-mono text-lg font-semibold text-b4l-text">
                  {i.nrOrdine}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-base text-b4l-text">{i.nume}</span>
                    {/*
                      Cupa spune ca a fost extras. La stand se cauta „cine e 155?" tocmai ca sa se
                      afle asta, iar raspunsul trebuie sa se vada de pe rand, nu dedus din faptul
                      ca are sau n-are buton.
                    */}
                    {i.castigator && (
                      <Trophy
                        size={14}
                        className="shrink-0 text-b4l-amber"
                        aria-label="câștigător"
                      />
                    )}
                    {/*
                      Ora inscrierii, langa nume. Alexandru: „adauga data si ora inscrierii".
                      La sute de randuri, ora spune imediat daca omul din fata ta s-a inscris acum
                      sau de dimineata — si daca intra in baza deschisa sau in una inchisa deja.
                    */}
                    <span className="shrink-0 font-mono text-xs font-normal text-b4l-text-dim">
                      {candS(i.inscrisLa)}
                    </span>
                  </span>
                  {i.telefon &&
                    (eTactil ? (
                      <a
                        href={`tel:${i.telefon}`}
                        className="block text-sm text-b4l-blue"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {i.telefon}
                      </a>
                    ) : (
                      // Pe desktop, text simplu: un link `tel:` deschide fereastra „Open Pick an
                      // app?" a browserului, care nu duce nicaieri si trebuie inchisa de fiecare
                      // data. La stand se lucreaza de pe telefon, unde linkul chiar suna.
                      <span className="block select-all text-sm text-b4l-text-muted">
                        {i.telefon}
                      </span>
                    ))}
                  {/* Ce a scris omul in formular — de citit inainte de predare (22.10.2026). */}
                  {i.observatii && (
                    <span className="mt-0.5 block whitespace-pre-line break-words text-sm text-b4l-text-muted">
                      <span className="font-medium text-b4l-text">Observații:</span> {i.observatii}
                    </span>
                  )}
                  {/*
                    Procesul-verbal al extragerii, la indemana celui care preda premiul. Alexandru:
                    „la Premii tombola PV extragere".

                    Sub nume, nu in dreapta: se citeste ca sa VERIFICI ca omul din fata ta e cel
                    extras — ora, numarul iesit, cati erau in baza —, deci sta langa numele lui, nu
                    langa butonul care preda. Apare si dupa predare: verificarea poate fi ceruta si
                    mai tarziu.
                  */}
                  {/*
                    Ceasul celor doua ore. Victor: „putem pune si un cronometru? Sa ne dam seama de
                    cele 2 ore". Art. 6.2 — termenul curge de la ora extragerii si nu se suspenda.

                    Dispare dupa predare: atunci intrebarea s-a inchis, iar un cronometru care merge
                    mai departe langa un premiu deja dat doar sperie.
                  */}
                  {/*
                    Cronologia premiului neridicat. Alexandru: „sa se vada clar ca au trecut 2 ore
                    intre data extragerii si data la care a fost marcat ca premiu neridicat".
                  */}
                  {tab === 'premiu' && i.extragereLa != null && i.neridicatLa != null && (
                    <span className="mt-0.5 block text-xs text-b4l-amber-dark">
                      Extras {ziSiOraS(i.extragereLa)} · neridicat {ziSiOraS(i.neridicatLa)} ·{' '}
                      <strong>{dupaCatTimp(i.extragereLa, i.neridicatLa).text}</strong>
                      {dupaCatTimp(i.extragereLa, i.neridicatLa).peste
                        ? ' — peste cele 2 ore (art. 6.2)'
                        : ' — sub cele 2 ore (art. 6.2)'}
                    </span>
                  )}
                  {tab === 'premiu' &&
                    i.extragereLa != null &&
                    i.neridicatLa == null &&
                    !i.premiuPredat && (
                      <span
                        className={`mt-0.5 block text-xs font-medium ${
                          ceasDouaOre(i.extragereLa).expirat
                            ? 'text-b4l-red'
                            : 'text-b4l-amber-dark'
                        }`}
                      >
                        ⏱ {ceasDouaOre(i.extragereLa).text}
                      </span>
                    )}
                  {tab === 'premiu' && i.extragereId != null && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        void deschideFisier(
                          `/marketing/sampling/extrageri/${i.extragereId}/pv`,
                        ).catch((err: Error) => toast.error(err.message))
                      }}
                      className="mt-0.5 text-xs text-b4l-blue underline"
                    >
                      PV extragere ↗
                    </button>
                  )}
                </span>
                {/*
                  Butonul apare doar cand actiunea chiar are ce da. Dupa predare se stinge: doua
                  apasari pe acelasi om ar scadea de doua ori din gestiune pentru un singur produs.

                  La PREMIU se cere in plus sa fi fost extras. Alexandru: „aici apare premiu
                  tombola la toti... pare in neregula. Nu ar trebui sa apara doar daca a castigat
                  la tombola?". Da — premiul se cuvine cui a iesit la extragere, si atat. Randul
                  ramane in lista, ca sa se poata cauta orice numar; butonul, nu.

                  Ascunderea nu e bariera, e doar ecran. Serverul refuza si el predarea unui premiu
                  catre cine n-a fost extras — vezi `cadou.service`.
                */}
                {/*
                  Premiul declarat neridicat nu se mai preda: art. 6.3, dreptul se pierde definitiv,
                  iar premiul a intrat in reextragere. Alexandru, vazand butonul inca activ pe un
                  rand marcat: „aici trebuie inactivat premiul, acum cred ca permite acordarea".

                  Ascunderea nu e bariera — serverul refuza si el —, e doar ca sa nu stea acolo un
                  buton care promite ce nu mai poate da.
                */}
                {/*
                  CE S-A DAT se arata mereu; ce SE POATE da atarna de configurare.

                  Alexandru, dupa ce a oprit cadoul: „la Recompensa story nu apare nimeni ca ar fi
                  primit vreun premiu, ceea ce nu e adevarat, foarte multi au primit". Eticheta
                  „predat" si linkul catre proces-verbal atarnau de aceeasi conditie ca butonul —
                  „actiunea are produse configurate" —, asa ca in clipa in care produsele s-au
                  terminat si au fost debifate, istoria a disparut odata cu ele.

                  Predarea s-a intamplat, are proces-verbal si bon de consum. O configurare de azi
                  n-are voie sa stearga de pe ecran ce s-a dat ieri.
                */}
                {(predatPe(i, tab) ||
                  (tab === 'premiu'
                    ? date.data?.arePremiu && i.castigator && i.neridicatLa == null
                    : date.data?.areCadou)) &&
                  !i.anulata && (
                    <span className="shrink-0">
                      {(tab === 'premiu' ? i.premiuPredat : i.cadouPredat) ? (
                        /*
                          Dupa predare, „predat" si documentul. Alexandru: „adauga PV-urile si in
                          zona aceasta".

                          Pana acum, ca sa vezi procesul-verbal al unei predari facute la stand
                          trebuia sa iesi din ecranul de stand, sa treci in Sampling si sa cauti
                          omul in lista de predari — pe telefon, in picioare, in mijlocul unui targ.

                          `rol` conteaza: art. 6.6 cere documente separate pentru kit si pentru
                          Cadoul Bonus, si asa pleaca si pe email. Fara el, foaia deschisa de aici
                          le-ar aduna pe amandoua si n-ar semana cu niciuna dintre cele trimise.
                        */
                        <span className="flex shrink-0 flex-col items-end gap-1">
                          <span className="flex items-center gap-1 rounded-full bg-b4l-green-light px-2 py-1 text-xs text-b4l-green-dark">
                            {/*
                              CUM a primit, nu doar CA a primit.

                              Alexandru, 30.09.2026: „cum se va diferentia predarea pe loc de
                              predarea la stand?". Un rand „predat" fara buton il lasa pe agent sa
                              creada ca l-a predat un coleg sau ca s-a stricat ceva. Asa stie ca
                              omul a semnat in formular si a plecat cu produsele.
                            */}
                            <Check size={13} />{' '}
                            {i.predatLaSemnatura && tab !== 'premiu'
                              ? 'predat la semnătură'
                              : 'predat'}
                          </span>
                          <button
                            onClick={() =>
                              void deschideFisier(
                                `/marketing/sampling/predari/${i.predareId}/pdf?rol=${tab}`,
                              ).catch((err: Error) => toast.error(err.message))
                            }
                            className="text-xs text-b4l-blue underline"
                          >
                            PV predare ↗
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => {
                            setSemnatura(null)
                            setStoryOk(false)
                            setProdusAles('')
                            setBifate(new Map())
                            setDeSemnat(i)
                          }}
                          /*
                          Cine a bifat in formular are butonul plin; cine n-a bifat il are
                          palid. Nu il ascund: omul poate cere cadoul si la stand, iar echipa
                          decide. Dar se vede din prima cine si-a asumat conditia inainte.
                        */
                          className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium ${
                            tab === 'premiu' || i.cadouCerut
                              ? 'bg-b4l-blue text-white'
                              : 'border border-b4l-border text-b4l-text-muted'
                          }`}
                          title={
                            tab === 'premiu'
                              ? 'Predă premiul tombolei'
                              : i.cadouCerut
                                ? 'A cerut cadoul în formular'
                                : 'N-a bifat cadoul în formular'
                          }
                        >
                          <Gift size={15} /> {tab === 'premiu' ? 'Premiu' : 'Cadou'}
                        </button>
                      )}
                    </span>
                  )}
                {/*
                  Se vede doar acolo unde chiar blocheaza ceva: bon emis, inca nedeclarat stornat,
                  inscriere neanulata. Textul spune ce DECLARI, nu ce apesi — CRM-ul n-are cum sa
                  verifice ce s-a intamplat in Expert.
                */}
                {eAdmin && i.bonEaDocNo && !i.bonStornat && !i.anulata && (
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          `Confirmi că bonul de consum ${i.bonEaDocNo} a fost stornat sau șters în ExpertAccounts?

CRM-ul nu poate verifica asta; rămâne înregistrat că ai confirmat tu.`,
                        )
                      )
                        bonStornat.mutate(i.predareId)
                    }}
                    disabled={bonStornat.isPending}
                    className="shrink-0 text-xs text-b4l-amber hover:underline disabled:opacity-40"
                    title={`Bon ${i.bonEaDocNo} — deblochează ștergerea`}
                  >
                    Bon stornat
                  </button>
                )}
                {eAdmin && (
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          `Ștergi definitiv înscrierea ${i.nrOrdine} — ${i.nume}?` +
                            '\n\nNu se mai poate reface. Dacă s-a predat deja ceva cu bon emis ' +
                            'în ExpertAccounts, ștergerea e oprită până stornezi bonul acolo.',
                        )
                      )
                        stergeInscrierea.mutate(i.predareId)
                    }}
                    disabled={stergeInscrierea.isPending}
                    className="shrink-0 text-xs text-b4l-red hover:underline disabled:opacity-40"
                    title="Șterge înscrierea (doar admin)"
                  >
                    Șterge
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="text-xs text-b4l-text-muted">
        {date.data ? `${date.data.inscrieri.length} înscriși · ${date.data.denumire}` : ''}
      </p>

      {deSemnat && (
        <Modal
          open
          onClose={() => setDeSemnat(null)}
          /* „Cadou" e cuvantul tombolei. In rest se preda o mostra, si asa trebuie sa scrie. */
          title={`${
            tab === 'premiu' ? 'Premiu' : tipActiune === 'tombola' ? 'Cadou' : 'Predare'
          } — ${deSemnat.nume}`}
        >
          <p className="text-sm text-b4l-text-muted">
            {tab === 'premiu'
              ? 'Semnează el, apoi confirmi. Pleacă procesul-verbal pe emailul lui și scade din gestiune.'
              : 'Întâi verifici, apoi alegi, apoi semnează el. La confirmare pleacă procesul-verbal pe emailul lui și scade din gestiune.'}
          </p>

          {/*
            Conditia de Story, NUMAI la cadou.

            Alexandru: „la Premiul tombolei apare mentionata conditia de story, dar pentru
            intrarea la tombola nu exista o astfel de conditie". Are dreptate — castigatorul a
            fost extras, atat. Cerandu-i o conditie de Story, i-am fi impus ceva ce regulamentul
            nu-i cere, si am fi legat cele doua mecanisme pe care art. 9.1 le tine separate.
          */}
          {tab === 'cadou' && conditieAcordare && (
            <label className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-b4l-border p-3">
              <span>
                <span className="block text-sm font-medium text-b4l-text">
                  Condiția e îndeplinită
                </span>
                <span className="block text-xs text-b4l-text-muted">{conditieAcordare}</span>
              </span>
              <input
                type="checkbox"
                checked={storyOk}
                onChange={(e) => setStoryOk(e.target.checked)}
                className="mt-0.5 h-6 w-6 shrink-0"
              />
            </label>
          )}

          {/*
            La CADOU se alege un produs; la PREMIU se da kitul intreg.

            Alexandru: „la Alege produsul ar trebui sa apara doar kitul, asta e singurul premiu si
            nu e nimic de ales". Kitul are sase componente care se dau impreuna — a le pune intr-o
            lista de ales insemna sa ceri operatorului sa aleaga una din sase, adica sa dea o
            sapca in loc de premiu.

            Deci la premiu nu se trimite `productId`, iar serverul preda toate produsele rolului.
            Se arata ce contine, ca omul sa poata verifica inainte de semnatura.
          */}
          {tab === 'premiu' ? (
            <div className="mt-3 rounded-lg border border-b4l-border p-3">
              <p className="text-sm font-medium text-b4l-text">Se acordă Kit-ul</p>
              <ul className="mt-2 divide-y divide-b4l-border">
                {produseRol.map((x) => (
                  <li
                    key={x.productId}
                    className="flex items-baseline justify-between gap-3 py-1.5 text-sm"
                  >
                    <span className="text-b4l-text">{x.denumire}</span>
                    <span className="shrink-0 font-mono text-xs text-b4l-text-muted">
                      ×{x.cantitate}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : eTombola ? (
            /*
              CADOUL DE TOMBOLA ramane cum era: o lista derulanta, un singur produs.

              Alexandru, 24.09.2026: „le las exact cum sunt". Fluxul asta e rodat in productie de
              la Sense4FIT, iar acolo cadoul chiar e unul singur — nu are ce cistiga dintr-un
              ecran de bifare.
            */
            produseRol.length > 1 && (
              <label className="mt-3 block">
                <span className="text-sm font-medium text-b4l-text">Produsul</span>
                <select
                  value={produsAles}
                  onChange={(e) => setProdusAles(e.target.value ? Number(e.target.value) : '')}
                  className="mt-1 h-12 w-full rounded-lg border border-b4l-border bg-white px-3 text-base"
                >
                  <option value="">Alege produsul</option>
                  {produseRol.map((x) => (
                    <option key={x.productId} value={x.productId}>
                      {x.denumire}
                    </option>
                  ))}
                </select>
              </label>
            )
          ) : (
            /*
              MOSTRELE: bifezi ce dai si scrii cat.

              Pana pe 24.09.2026 era tot o lista derulanta, cu un singur produs. La farmacie omul
              pleaca de multe ori cu doua-trei mostre, iar agentul nu le putea trece pe acelasi
              proces-verbal.

              Cantitatea porneste de la 1 — nu de la cea configurata pe actiune, care inseamna cat
              s-a alocat pe TOATA actiunea. Langa fiecare rand scrie cat a plecat din alocare, ca
              sa se vada inainte de apasare, nu la inchiderea actiunii.
            */
            <div className="mt-3 rounded-lg border border-b4l-border p-3">
              <p className="text-sm font-medium text-b4l-text">Ce primește</p>
              <ul className="mt-2 divide-y divide-b4l-border">
                {produseRol.map((x) => {
                  const bifat = bifate.has(x.productId)
                  const cantitate = bifate.get(x.productId) ?? 1
                  const ramas = x.cantitate - x.predat
                  return (
                    <li key={x.productId} className="py-2">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={bifat}
                          onChange={(e) => {
                            const urmator = new Map(bifate)
                            if (e.target.checked) urmator.set(x.productId, 1)
                            else urmator.delete(x.productId)
                            setBifate(urmator)
                          }}
                          className="h-6 w-6 shrink-0"
                        />
                        <span className="flex-1 text-sm text-b4l-text">{x.denumire}</span>
                        {bifat && (
                          <input
                            type="number"
                            inputMode="numeric"
                            min={1}
                            max={1000}
                            value={cantitate}
                            onChange={(e) => {
                              const n = Number(e.target.value)
                              const urmator = new Map(bifate)
                              urmator.set(x.productId, Number.isFinite(n) && n > 0 ? n : 1)
                              setBifate(urmator)
                            }}
                            className="h-10 w-20 shrink-0 rounded-lg border border-b4l-border px-2 text-center text-base"
                            aria-label={`Cantitate ${x.denumire}`}
                          />
                        )}
                      </div>
                      {/*
                        Alocarea si cat a mai ramas din ea. Doar informativ: predarea nu se
                        blocheaza peste plan (Alexandru, 24.09.2026). Bariera reala e stocul din
                        gestiune, verificat pe server — un plan depasit inseamna o actiune care a
                        mers mai bine decat s-a estimat, nu o greseala.
                      */}
                      <p className="mt-0.5 pl-9 text-xs text-b4l-text-muted">
                        alocat {x.cantitate} · predat {x.predat} ·{' '}
                        {ramas >= 0 ? `rămas ${ramas}` : `peste plan cu ${-ramas}`}
                      </p>
                      {bifat && cantitate > ramas && (
                        <p className="mt-0.5 pl-9 text-xs text-b4l-amber-dark">
                          Depășești cantitatea alocată acțiunii. Poți preda, dar anunță echipa de
                          marketing.
                        </p>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          <div className="mt-3">
            <SemnaturaPad onChange={setSemnatura} disabled={predaCadou.isPending} />
          </div>
          {/*
            De ce nu se poate confirma inca. Alexandru: „am incercat sa simulez o acordare de cadou,
            dar nu se activa butonul de confirmare dupa semnare".

            Cerintele erau trei — conditia bifata, produsul ales, semnatura —, dar butonul stins nu
            spunea care lipseste. La stand, in picioare, cu omul in fata, un buton mort e o
            defectiune; abia dupa ce spune ce-i lipseste devine o instructiune.
          */}
          {(() => {
            const lipsesc = [
              tab === 'cadou' && conditieAcordare && !storyOk ? 'bifează condiția' : null,
              tab === 'cadou' && eTombola && produseRol.length > 1 && produsAles === ''
                ? 'alege produsul'
                : null,
              tab === 'cadou' && !eTombola && bifate.size === 0 ? 'bifează ce primește' : null,
              !semnatura ? 'semnează în chenar' : null,
            ].filter(Boolean)
            return lipsesc.length > 0 ? (
              <p className="mt-3 text-sm text-b4l-amber-dark">
                Ca să poți confirma: {lipsesc.join(', ')}.
              </p>
            ) : null
          })()}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeSemnat(null)}>
              Anulează
            </Button>
            <Button
              disabled={
                !semnatura ||
                // Conditia se cere doar la cadou; castigatorul a fost extras, atat.
                (tab === 'cadou' && Boolean(conditieAcordare) && !storyOk) ||
                // Alegerea, tot doar la cadou: premiul se preda intreg.
                (tab === 'cadou' && eTombola && produseRol.length > 1 && produsAles === '') ||
                // La mostre trebuie bifat cel putin un produs: altfel n-ar pleca nimic.
                (tab === 'cadou' && !eTombola && bifate.size === 0) ||
                predaCadou.isPending
              }
              onClick={() =>
                semnatura &&
                predaCadou.mutate({
                  predareId: deSemnat.predareId,
                  semnatura,
                  // La premiu nu exista conditie de verificat, deci nu se consemneaza una.
                  /*
                    Fara conditie scrisa nu se trimite nimic: serverul retine `cadouStoryOk` ca
                    declaratie a consultantului, iar un „da" pentru o conditie inexistenta ar fi o
                    declaratie despre nimic, ramasa in evidenta.
                  */
                  storyOk: tab === 'cadou' && conditieAcordare ? storyOk : undefined,
                  rol: tab,
                  /*
                    La MOSTRE pleaca lista bifata, cu cantitatile scrise de agent.
                  */
                  produse:
                    tab === 'cadou' && !eTombola
                      ? [...bifate].map(([productId, cantitate]) => ({ productId, cantitate }))
                      : undefined,
                  /*
                    La PREMIU nu se trimite `productId`: serverul preda toate produsele rolului,
                    adica kitul intreg. La cadoul de tombola se trimite unul singur — iar cand
                    rolul are un singur produs, acela se alege singur si nu se mai intreaba.
                  */
                  productId:
                    tab === 'premiu' || !eTombola
                      ? undefined
                      : produsAles !== ''
                        ? produsAles
                        : produseRol.length === 1
                          ? produseRol[0].productId
                          : undefined,
                })
              }
            >
              {predaCadou.isPending ? 'Se trimite…' : 'Confirmă predarea'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
